import { EnvelopeIcon, PhoneIcon } from '@heroicons/react/24/outline';
import { useState, useEffect } from 'react';

import { CONTACT } from '../config/contact';

import TruckRunnerGame from './TruckRunnerGame';

interface MaintenancePageProps {
  /**
   * Custom maintenance message from database
   * If not provided, displays default Swedish maintenance message
   */
  message?: string | null;
}

/**
 * MaintenancePage Component
 *
 * Displays a branded maintenance message when the application is under maintenance.
 * Controlled by database and shown only in production with production nShift credentials.
 *
 * @param message - Optional custom message from Adminpanel
 */
export default function MaintenancePage({ message }: MaintenancePageProps) {
  const [showGame, setShowGame] = useState(false);

  // Parse custom message for display
  // If message contains line breaks, split into paragraphs
  const messageLines = message
    ? message.split('\n').filter(line => line.trim().length > 0)
    : null;

  // Handle spacebar to show game (only when game is not already visible)
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !showGame) {
        e.preventDefault();
        setShowGame(true);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [showGame]);
  return (
    <div className="min-h-screen bg-[#F9FCFC] flex items-center justify-center p-6">
      <div className="max-w-lg w-full text-center space-y-8">
        {/* Simple Wrench Icon */}
        <div className="inline-flex items-center justify-center">
          <svg
            className="w-12 h-12 text-stort-red"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z"
            />
          </svg>
        </div>

        {/* Lighter Heading */}
        <h1 className="text-page-title font-medium text-gray-900">
          Underhåll pågår
        </h1>

        {/* Clean Message */}
        <div className="space-y-3 text-gray-600 max-w-md mx-auto">
          {messageLines && messageLines.length > 0 ? (
            // Display custom message from admin
            messageLines.map((line, index) => (
              <p key={index} className="leading-relaxed">
                {line}
              </p>
            ))
          ) : (
            // Default message
            <>
              <p className="leading-relaxed">
                STORTs Bokningsportal är för tillfället nere för underhåll och uppdateringar.
              </p>
              <p className="leading-relaxed">
                Vi arbetar för att göra systemet bättre och kommer snart vara tillbaka.
              </p>
            </>
          )}
        </div>

        {/* Subtle Divider */}
        <div className="flex items-center justify-center gap-3 max-w-xs mx-auto">
          <div className="h-px flex-1 bg-gray-200" />
          <div className="h-px flex-1 bg-gray-200" />
        </div>

        {/* Contact Information */}
        <div className="space-y-3 max-w-sm mx-auto">
          <p className="text-body font-medium text-gray-700">
            Kontakta oss vid brådskande ärenden
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
            {/* Email */}
            <a
              href={`mailto:${CONTACT.email}`}
              className="flex items-center gap-2 text-gray-600 hover:text-stort-red transition-colors duration-200 group"
            >
              <EnvelopeIcon className="w-5 h-5 flex-shrink-0" />
              <span className="text-body font-medium">{CONTACT.email}</span>
            </a>

            {/* Phone */}
            <a
              href={CONTACT.phoneHref}
              className="flex items-center gap-2 text-gray-600 hover:text-stort-red transition-colors duration-200 group"
            >
              <PhoneIcon className="w-5 h-5 flex-shrink-0" />
              <span className="text-body font-medium">{CONTACT.phone}</span>
            </a>
          </div>
        </div>

        {/* Three Animated Dots */}
        <div className="flex justify-center gap-2">
          <div className="w-1.5 h-1.5 bg-stort-red rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
          <div className="w-1.5 h-1.5 bg-stort-red rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
          <div className="w-1.5 h-1.5 bg-stort-red rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>

        {/* Game hint */}
        {!showGame && (
          <button
            onClick={() => setShowGame(true)}
            className="text-meta text-gray-400 animate-pulse hover:text-gray-600 hover:scale-105 active:scale-95 transition-all duration-200 cursor-pointer"
          >
            Tryck mellanslag eller klicka här för att spela ett spel medan du väntar
          </button>
        )}

        {/* Game Section */}
        {showGame && (
          <div className="mt-8">
            <TruckRunnerGame onClose={() => setShowGame(false)} />
          </div>
        )}
      </div>
    </div>
  );
}
